/**
 * Motera API client + retry-logic.
 * Volgens Steven's spec: 3x network-retry exponential (1s, 2s, 4s),
 * 1x retry op 5xx na 3s, 4xx → direct foutmelding.
 */
import axios from "axios";
import { ontbrekendeVeldenZin } from "./namen";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API_BASE,
  timeout: 15000,
  headers: { "Content-Type": "application/json" },
});

const _sleep = (ms) => new Promise((r) => setTimeout(r, ms));

/** Wikkel een axios-call met retry-logica volgens spec. */
export async function withRetry(fn, { signal } = {}) {
  const networkDelays = [1000, 2000, 4000];
  let retried5xx = false;

  for (let attempt = 0; attempt <= 3; attempt++) {
    try {
      return await fn();
    } catch (err) {
      if (signal?.aborted) throw err;
      const status = err?.response?.status;
      if (status && status >= 400 && status < 500) {
        throw err; // 4xx → direct
      }
      if (status && status >= 500 && status < 600) {
        if (retried5xx) throw err;
        retried5xx = true;
        await _sleep(3000);
        continue;
      }
      // network-err (no response)
      if (!err.response && attempt < 3) {
        await _sleep(networkDelays[attempt]);
        continue;
      }
      throw err;
    }
  }
  throw new Error("retry_uitgeput");
}

/**
 * Map backend error → leesbare Nederlandse string.
 *
 * @param {object} err   axios-error
 * @param {object} [opts]
 * @param {object} [opts.schema]  optioneel v4 `vragen_json`; als meegegeven worden
 *   de veld-id's uit een 422-`detail.velden` naar hun vraagtekst vertaald, zodat
 *   de melding het/de foute veld(en) benoemt i.p.v. de kale generieke tekst.
 */
export function formatApiError(err, opts = {}) {
  const status = err?.response?.status;
  const detail = err?.response?.data?.detail;
  const code = detail?.error_code;
  const msg = detail?.message || detail || err.message;

  if (status === 404 && (code === "token_ongeldig" || code === "aanmeldcode_ongeldig" || code === "entiteit_niet_gevonden")) {
    return {
      kind: "verlopen",
      message: "Deze link is niet meer geldig. Neem contact op met uw werkgever via info@fulkraworks.nl",
    };
  }
  if (status === 409 && (code === "followup_al_ingevuld" || code === "baseline_al_ingevuld")) {
    return {
      kind: "dubbel",
      message: "U heeft deze vragenlijst al ingevuld. Bedankt!",
    };
  }
  if (status === 410) {
    return {
      kind: "gepauzeerd",
      message: "Uw werkgever heeft het programma gepauzeerd. Neem contact op via info@fulkraworks.nl",
    };
  }
  if (status === 422 && code === "gegevens_niet_compleet") {
    // De servergrens van blok 4: geen nulmeting zonder complete gegevens én toestemming.
    //
    // Deze fout viel tot nu toe in de algemene 422-tak hieronder en las als "Sommige
    // velden zijn niet correct ingevuld. Controleer de gemarkeerde velden." — terwijl er
    // niets gemarkeerd stond en het probleem niet in de vragenlijst zat maar op de
    // gegevenspagina. Iemand die dat leest gaat zijn antwoorden nakijken en vindt niets.
    //
    // De ontbrekende velden komen als kale kolomnamen binnen (`avg_toestemming_op`); die
    // horen niet op het scherm (ADR-065), dus ze gaan door dezelfde vertaling als op de
    // gegevenspagina zelf.
    const velden = detail?.ontbrekende_velden || [];
    const zin = ontbrekendeVeldenZin(velden);
    return {
      kind: "gegevens_niet_compleet",
      velden,
      message: zin
        ? `Er ontbreken nog gegevens: ${zin}. Vul die eerst aan op uw gegevenspagina, daarna kunt u de vragenlijst invullen.`
        : "Er ontbreken nog gegevens. Vul die eerst aan op uw gegevenspagina, daarna kunt u de vragenlijst invullen.",
    };
  }
  if (status === 422) {
    // De backend-validator (antwoord_validator.py) levert detail.velden =
    // { veld_id: reden }. Benoem die velden in de melding — bij voorkeur met hun
    // vraagtekst uit het schema — zodat de gebruiker weet wat er mis is.
    const veldenFout = detail?.velden;
    const veldIds = veldenFout && typeof veldenFout === "object" ? Object.keys(veldenFout) : [];
    if (veldIds.length) {
      const velddef = opts?.schema?.velden || {};
      const namen = veldIds.map((id) => velddef?.[id]?.vraag || id);
      return {
        kind: "validatie",
        velden: veldIds,
        message:
          namen.length === 1
            ? `Deze vraag is niet correct ingevuld: ${namen[0]}. Controleer uw antwoord.`
            : `Deze vragen zijn niet correct ingevuld: ${namen.join(" · ")}. Controleer uw antwoorden.`,
      };
    }
    return { kind: "validatie", message: "Sommige velden zijn niet correct ingevuld. Controleer de gemarkeerde velden." };
  }
  if (!err.response) {
    return { kind: "netwerk", message: "Verbinding verloren. Controleer uw internet en probeer opnieuw." };
  }
  return { kind: "onbekend", message: typeof msg === "string" ? msg : "Er ging iets mis. Probeer opnieuw of neem contact op." };
}
